import React, { useState } from 'react';
import { FiMail, FiPhone, FiMapPin, FiSend } from 'react-icons/fi';
import { AiOutlineWhatsApp, AiOutlineInstagram } from 'react-icons/ai';
import { useDarkMode } from '../context/DarkModeContext';
import { motion } from 'framer-motion';

const Contact = () => {
  const { isDark } = useDarkMode();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      // Get the backend URL - use relative URL in production
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      console.log('📧 Submitting form to:', backendUrl + '/api/form/submit');
      
      const res = await fetch(backendUrl + '/api/form/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        console.log('✅ Form submitted successfully!', data);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        console.error('❌ Form submission failed:', data.error);
        setErrorMessage(data.error || 'Failed to submit form');
        setSubmitStatus('error');
        
        // Clear error message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (err) {
      console.error('❌ Network error:', err);
      setErrorMessage('Unable to connect to server. Make sure the backend is running.');
      setSubmitStatus('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FiPhone,
      label: 'Phone',
      value: '03047259307',
      href: 'tel:03047259307',
    },
    {
      icon: FiMail,
      label: 'Email',
      value: 'ahtashamspeaks@gmail.com',
      href: 'mailto:ahtashamspeaks@gmail.com',
    },
    {
      icon: AiOutlineWhatsApp,
      label: 'WhatsApp',
      value: '03047259307',
      href: 'https://wa.me/923047259307',
    },
    {
      icon: FiMapPin,
      label: 'Location',
      value: 'Lahore, Pakistan',
      href: null,
    },
  ];

  const socialLinks = [
    {
      icon: AiOutlineInstagram,
      href: 'https://www.instagram.com/mediasociety.agency?igsh=Z282bjA2ZWEyOHg5&utm_source=qr',
      label: '@mediasociety.agency',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <section id="contact" className={`relative w-full py-20 lg:py-32 transition-colors duration-500 ${
      isDark ? 'bg-dark-primary' : 'bg-light-secondary'
    }`}>
      {/* Background Decorations */}
      <motion.div
        className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      ></motion.div>
      <motion.div
        className={`absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl -z-10 ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 7, repeat: Infinity, delay: 1 }}
      ></motion.div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          <motion.p
            className="text-primary-gold font-montserrat font-semibold uppercase tracking-widest text-sm mb-2"
            animate={{ letterSpacing: [0, '0.1em', 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Get In Touch
          </motion.p>
          <motion.h2
            className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
              isDark ? 'text-white' : 'text-black'
            }`}
            variants={itemVariants}
          >
            Request Your Free Audit
          </motion.h2>
          <motion.p
            className={`font-poppins max-w-2xl mx-auto text-lg transition-colors ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            variants={itemVariants}
          >
            Let's discuss how we can transform your brand's social media presence
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: '-100px' }}
        >
          {/* Contact Information */}
          <motion.div className="space-y-6" variants={itemVariants}>
            {/* Quick Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <motion.a
                    key={idx}
                    href={item.href || '#'}
                    onClick={(e) => !item.href && e.preventDefault()}
                    className={`flex items-start gap-4 p-6 rounded-xl border transition-all duration-300 group ${
                      isDark
                        ? 'bg-dark-secondary border-primary-gold/10 hover:border-primary-gold/40 hover:shadow-gold-glow'
                        : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40 hover:shadow-lg'
                    } ${item.href ? 'cursor-pointer' : ''}`}
                    variants={itemVariants}
                    whileHover={{ y: -6, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div
                      className={`flex items-center justify-center h-12 w-12 rounded-lg group-hover:scale-110 transition-all flex-shrink-0 mt-1 ${
                        isDark
                          ? 'bg-primary-gold/10 group-hover:bg-primary-gold/20'
                          : 'bg-primary-gold/15 group-hover:bg-primary-gold/25'
                      }`}
                      whileHover={{ rotate: 10 }}
                    >
                      <IconComponent className="h-6 w-6 text-primary-gold" />
                    </motion.div>
                    <div>
                      <p className={`font-montserrat font-bold mb-1 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                        {item.label}
                      </p>
                      <p className={`font-poppins text-sm group-hover:text-primary-gold transition-colors ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {item.value}
                      </p>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            {/* Social Media Links */}
            <motion.div variants={itemVariants}>
              <p className={`font-montserrat font-bold mb-4 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                Follow Us
              </p>
              <motion.div className="flex gap-4" variants={containerVariants}>
                {socialLinks.map((social, idx) => {
                  const IconComponent = social.icon;
                  return (
                    <motion.a
                      key={idx}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-full border flex items-center justify-center text-primary-gold transition-all duration-300 ${
                        isDark
                          ? 'bg-primary-gold/10 border-primary-gold/20 hover:bg-primary-gold hover:text-black hover:border-primary-gold'
                          : 'bg-primary-gold/15 border-primary-gold/30 hover:bg-primary-gold hover:text-black hover:border-primary-gold'
                      }`}
                      whileHover={{ scale: 1.15, rotate: 10, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      title={social.label}
                      animate={{
                        y: [0, -3, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: idx * 0.2,
                      }}
                    >
                      <IconComponent className="w-6 h-6" />
                    </motion.a>
                  );
                })}
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-2"
            variants={itemVariants}
          >
            <motion.form
              onSubmit={handleSubmit}
              className={`space-y-6 p-8 border rounded-2xl transition-colors ${
                isDark
                  ? 'bg-dark-secondary border-primary-gold/10'
                  : 'bg-light-tertiary border-primary-gold/20'
              }`}
              whileInView={{ scale: 1 }}
            >
              {/* Name Field */}
              <motion.div variants={itemVariants}>
                <label className={`block font-montserrat font-semibold mb-3 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                  Full Name
                </label>
                <motion.input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg font-poppins focus:outline-none focus:border-primary-gold transition-all placeholder-gray-500 ${
                    isDark
                      ? 'bg-dark-primary text-white border-primary-gold/20 focus:shadow-gold-glow'
                      : 'bg-light-primary text-black border-primary-gold/30'
                  }`}
                  placeholder="John Doe"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              {/* Email Field */}
              <motion.div variants={itemVariants}>
                <label className={`block font-montserrat font-semibold mb-3 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                  Email Address
                </label>
                <motion.input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg font-poppins focus:outline-none focus:border-primary-gold transition-all placeholder-gray-500 ${
                    isDark
                      ? 'bg-dark-primary text-white border-primary-gold/20 focus:shadow-gold-glow'
                      : 'bg-light-primary text-black border-primary-gold/30'
                  }`}
                  placeholder="john@example.com"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              {/* Phone Field */}
              <motion.div variants={itemVariants}>
                <label className={`block font-montserrat font-semibold mb-3 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                  Phone Number
                </label>
                <motion.input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg font-poppins focus:outline-none focus:border-primary-gold transition-all placeholder-gray-500 ${
                    isDark
                      ? 'bg-dark-primary text-white border-primary-gold/20 focus:shadow-gold-glow'
                      : 'bg-light-primary text-black border-primary-gold/30'
                  }`}
                  placeholder="+92 301 9681065"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              {/* Subject Field */}
              <motion.div variants={itemVariants}>
                <label className={`block font-montserrat font-semibold mb-3 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                  Subject
                </label>
                <motion.input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg font-poppins focus:outline-none focus:border-primary-gold transition-all placeholder-gray-500 ${
                    isDark
                      ? 'bg-dark-primary text-white border-primary-gold/20 focus:shadow-gold-glow'
                      : 'bg-light-primary text-black border-primary-gold/30'
                  }`}
                  placeholder="Social Media Campaign Inquiry"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              {/* Message Field */}
              <motion.div variants={itemVariants}>
                <label className={`block font-montserrat font-semibold mb-3 transition-colors ${isDark ? 'text-white' : 'text-black'}`}>
                  Message
                </label>
                <motion.textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className={`w-full px-4 py-3 border rounded-lg font-poppins focus:outline-none focus:border-primary-gold transition-all resize-none placeholder-gray-500 ${
                    isDark
                      ? 'bg-dark-primary text-white border-primary-gold/20 focus:shadow-gold-glow'
                      : 'bg-light-primary text-black border-primary-gold/30'
                  }`}
                  placeholder="Tell us about your project..."
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <motion.div
                  className={`p-4 border rounded-lg ${
                    isDark
                      ? 'bg-green-900/20 border-green-600'
                      : 'bg-green-100 border-green-300'
                  }`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <p className={`${isDark ? 'text-green-400' : 'text-green-700'} font-poppins`}>
                    ✅ Your message has been sent successfully! We'll review it and get back to you soon.
                  </p>
                </motion.div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <motion.div
                  className={`p-4 border rounded-lg ${
                    isDark
                      ? 'bg-red-900/20 border-red-600'
                      : 'bg-red-100 border-red-300'
                  }`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <p className={`${isDark ? 'text-red-400' : 'text-red-700'} font-poppins`}>
                    ❌ {errorMessage || 'Failed to send your message. Please try again.'}
                  </p>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 bg-primary-gold text-black font-montserrat font-bold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark ? 'hover:shadow-gold-glow-lg' : 'hover:shadow-lg'
                }`}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    Send Message <FiSend />
                  </>
                )}
              </motion.button>

              <motion.p
                className={`font-poppins text-sm text-center transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                We typically respond within 24 hours
              </motion.p>
            </motion.form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
